/**
 * Screen-space layout for DOM overlays drawn over the viewport: the selection
 * bracket and the axis triad. Pure functions of the camera, so they're
 * testable without a renderer.
 */
import { aabbCorners } from "../../core/math/aabb";
import type { Aabb } from "../../core/math/aabb";
import { vec3Dot } from "../../core/math/vec3";
import type { Vec3 } from "../../core/math/vec3";
import { forwardOf, projectPoint, rightOf, upOf } from "./cameraMath";
import type { ViewCamera } from "./cameraMath";

export interface ScreenRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/**
 * The screen rectangle enclosing a world-space box, or null if none of it is in
 * front of the camera. Corners behind the camera are ignored so a box the
 * camera sits inside doesn't produce a wildly flipped rectangle.
 */
export function screenRectOfBounds(
  camera: ViewCamera,
  bounds: Aabb,
  width: number,
  height: number,
): ScreenRect | null {
  const points = aabbCorners(bounds)
    .map((corner) => projectPoint(camera, corner, width, height))
    .filter((point) => point.visible);
  if (points.length === 0) return null;

  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const left = Math.min(...xs);
  const top = Math.min(...ys);
  return { left, top, width: Math.max(...xs) - left, height: Math.max(...ys) - top };
}

export interface TriadAxis {
  readonly axis: "x" | "y" | "z";
  /** Screen-space direction; +x right, +y down. Length shrinks as the axis points at the viewer. */
  readonly dx: number;
  readonly dy: number;
  /** How far the axis points away from the viewer; used for draw order. */
  readonly depth: number;
}

const AXES: readonly (readonly ["x" | "y" | "z", Vec3])[] = [
  ["x", [1, 0, 0]],
  ["y", [0, 1, 0]],
  ["z", [0, 0, 1]],
];

/** World axes as seen by the camera, farthest first so nearer axes draw on top. */
export function triadAxes(camera: ViewCamera): TriadAxis[] {
  const right = rightOf(camera);
  const up = upOf(camera);
  const forward = forwardOf(camera);

  return AXES.map(([axis, direction]) => ({
    axis,
    dx: vec3Dot(direction, right),
    dy: -vec3Dot(direction, up),
    depth: vec3Dot(direction, forward),
  })).sort((a, b) => b.depth - a.depth);
}
